import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { ImageDefaults } from '@/entities/agent';
import type { ImageModel, ImageOptions } from './ImageGenerationOptions.types';
import {
  getOptionsForModel,
  isImageSeed,
  isKnownImageModel,
  optionsForImageModel,
} from './ImageOptionsHelpers';

type Options = Record<string, unknown> | undefined;

/**
 * The image options a composer starts on for an agent built with Generate image: its card's
 * defaults, which the image widget's Edit reads as the selected image model. Written into the
 * composer's options whenever they hold no image settings, whatever else they hold, and followed
 * when the agent's defaults change while the person has not picked.
 */
export function useImageSeed(
  defaults: ImageDefaults | undefined,
  options: Options,
  updateOptions: (update: (current: Options) => Options) => void,
) {
  const seed = useMemo(
    () =>
      defaults && isKnownImageModel(defaults.settings.model)
        ? getOptionsForModel(defaults.settings.model, defaults as Partial<ImageOptions>)
        : undefined,
    [defaults],
  );

  const seeded = useRef(seed);
  useEffect(() => {
    const before = seeded.current;
    seeded.current = seed;
    if (!seed) return;
    const unpicked =
      !options?.settings ||
      (before !== undefined && before !== seed && isImageSeed(options, before));
    if (unpicked && !isImageSeed(options ?? {}, seed)) {
      updateOptions((current) => ({ ...current, ...seed }));
    }
  }, [seed, options, updateOptions]);

  /** Whether a send's options are still the defaults, rather than a pick of the person's. */
  const atSeed = useCallback(
    (inputs: Record<string, unknown>) => seed !== undefined && isImageSeed(inputs, seed),
    [seed],
  );

  /**
   * The options after the chip picks a model. Back on the defaults' own model it starts from the
   * defaults, so leaving that model and coming back does not lose the node's settings.
   */
  const pickModel = useCallback(
    (current: Options, model: ImageModel): Record<string, unknown> => {
      const from = { ...seed, ...current } as ImageOptions;
      const picked =
        seed && model === seed.settings.model
          ? { ...seed, variations: from.variations, mask: from.mask }
          : optionsForImageModel(from, model);
      return { ...current, ...picked };
    },
    [seed],
  );

  return { atSeed, pickModel };
}
